// Простая система кеширования для API запросов
class APICache {
    constructor(defaultTTL = 5 * 60 * 1000) { // 5 минут по умолчанию
        this.cache = new Map();
        this.defaultTTL = defaultTTL;
    }

    // Создает ключ для кеша на основе URL и параметров
    createKey(url, params = {}) {
        const sortedParams = Object.keys(params)
            .sort()
            .reduce((result, key) => {
                if (params[key] !== undefined && params[key] !== null) {
                    result[key] = params[key];
                }
                return result;
            }, {});
        
        return `${url}?${JSON.stringify(sortedParams)}`;
    }

    // Получает данные из кеша
    get(url, params = {}) {
        const key = this.createKey(url, params);
        const cached = this.cache.get(key);
        
        if (!cached) {
            return null;
        }

        // Проверяем, не истек ли срок действия
        if (Date.now() > cached.expires) {
            this.cache.delete(key);
            return null;
        }

        console.log(`🚀 Cache HIT for: ${key}`);
        return cached.data;
    }

    // Сохраняет данные в кеш
    set(url, params = {}, data, ttl = this.defaultTTL) {
        const key = this.createKey(url, params);
        const expires = Date.now() + ttl;
        
        this.cache.set(key, {
            data,
            expires,
            timestamp: Date.now()
        });

        console.log(`💾 Cache SET for: ${key} (TTL: ${ttl}ms)`);
        
        // Автоматическая очистка старых записей
        this.cleanup();
    }

    // Инвалидирует кеш по паттерну URL
    invalidate(urlPattern) {
        const keysToDelete = [];
        
        for (const key of this.cache.keys()) {
            if (key.includes(urlPattern)) {
                keysToDelete.push(key);
            }
        }
        
        keysToDelete.forEach(key => {
            this.cache.delete(key);
            console.log(`🗑️ Cache INVALIDATED: ${key}`);
        });
        
        return keysToDelete.length;
    }

    // Очищает весь кеш
    clear() {
        const size = this.cache.size;
        this.cache.clear();
        console.log(`🧹 Cache CLEARED: ${size} items removed`);
        return size;
    }

    // Удаляет истекшие записи
    cleanup() {
        const now = Date.now();
        const keysToDelete = [];
        
        for (const [key, value] of this.cache.entries()) {
            if (now > value.expires) {
                keysToDelete.push(key);
            }
        }
        
        keysToDelete.forEach(key => this.cache.delete(key));
        
        if (keysToDelete.length > 0) {
            console.log(`🧹 Cache CLEANUP: ${keysToDelete.length} expired items removed`);
        }
    }

    // Получает статистику кеша
    getStats() {
        const now = Date.now();
        let active = 0;
        let expired = 0;
        
        for (const value of this.cache.values()) {
            if (now > value.expires) {
                expired++;
            } else {
                active++;
            }
        }
        
        return {
            total: this.cache.size,
            active,
            expired
        };
    }
}

// Создаем глобальный экземпляр кеша
const apiCache = new APICache();

// Кешированная версия axios.get
export const cachedAxiosGet = async (url, config = {}) => {
    const { params, ...axiosConfig } = config;
    const cacheTTL = config.cacheTTL || 5 * 60 * 1000; // 5 минут по умолчанию
    
    // Проверяем кеш
    const cachedData = apiCache.get(url, params);
    if (cachedData) {
        return { data: cachedData };
    }
    
    // Если в кеше нет, делаем запрос
    try {
        const axios = (await import('axios')).default;
        const response = await axios.get(url, { params, ...axiosConfig });
        
        // Сохраняем в кеш только успешные ответы
        if (response.status === 200) {
            apiCache.set(url, params, response.data, cacheTTL);
        }
        
        return response;
    } catch (error) {
        // При ошибке не кешируем
        throw error;
    }
};

// Экспортируем функции для управления кешем
export const invalidateCache = (urlPattern) => apiCache.invalidate(urlPattern);
export const clearCache = () => apiCache.clear();
export const getCacheStats = () => apiCache.getStats();

export default apiCache;
